import { useState } from "react";
import PrimaryButton from "../../buttons/PrimaryButton";
import { Input } from "../../@shadcn/components/ui/input";
import { CaretLeft } from "@phosphor-icons/react";
import { GiftCardInputTypeType } from "../types";
import { useGiftCardContext } from "../context/GiftProvider";
import {
  GIFT_CARD_INPUT_FOCUS_CLASS,
  getGiftCardInputFocusStyle,
} from "../commons/constants";

const DEFAULT_ACCENT_COLOR = "#432a2e";
const DEFAULT_TITLE = "Add Balance";
const DEFAULT_PLACEHOLDER = "16 digit code";
const DEFAULT_SUBMIT_LABEL = "Add";

export interface GiftCardAddBalanceProps {
  iconUrl?: string;
  providerLabel: string;
  showCheckBadge?: boolean;
  inputType?: GiftCardInputTypeType;
  maxLength?: number;
  placeholder?: string;
  accentColor?: string;
  title?: string;
  submitLabel?: string;
}

const GiftCardAddBalance = ({
  iconUrl,
  providerLabel,
  showCheckBadge = false,
  inputType,
  maxLength,
  placeholder = DEFAULT_PLACEHOLDER,
  accentColor = DEFAULT_ACCENT_COLOR,
  title = DEFAULT_TITLE,
  submitLabel = DEFAULT_SUBMIT_LABEL,
}: GiftCardAddBalanceProps) => {
  // Local to this screen: the code is spent on the top-up call and never leaves the page.
  const [code, setCode] = useState("");
  const { actionState, setActiveStep, rechargeActiveWallet, setActionState, activeProvider } =
    useGiftCardContext();
  const { loading, error } = actionState.RECHARGING;

  const handleChange = (raw: string) => {
    setCode(inputType === "NUMERIC" ? raw.replace(/\D/g, "") : raw);
    if (error && activeProvider) {
      setActionState(activeProvider.provider, "RECHARGING", { error: null });
    }
  };

  const handleSubmit = async () => {
    const loaded = await rechargeActiveWallet(code);
    if (loaded) {
      setCode("");
      setActiveStep("CARD_ENTRY");
    }
  };

  return (
    <div className="ui-flex ui-w-full ui-flex-col ui-rounded-t-2xl ui-bg-white">
      <div className="ui-flex ui-w-full ui-items-center ui-gap-3 ui-px-5 ui-py-4">
        <button
          type="button"
          onClick={() => setActiveStep("CARD_ENTRY")}
          aria-label="Go back"
          className="ui-flex ui-size-8 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-lg ui-bg-black/[0.04] ui-text-zinc-500 hover:ui-bg-black/[0.08] active:ui-bg-black/[0.08]"
        >
          <CaretLeft size={20} aria-hidden />
        </button>

        <p className="ui-min-w-0 ui-flex-1 ui-text-text-lg-semibold ui-text-zinc-800">
          {title}
        </p>
      </div>

      <div className="ui-flex ui-w-full ui-flex-col ui-gap-6 ui-px-5 ui-pb-6">
        <div className="ui-flex ui-w-full ui-flex-col ui-gap-1">
          <Input
            variant="filled"
            value={code}
            onChange={(event) => handleChange(event.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            inputMode={inputType === "NUMERIC" ? "numeric" : "text"}
            error={Boolean(error)}
            aria-label={placeholder}
            className={`ui-h-12 ui-text-text-md-regular placeholder:ui-text-[#9f9fa9] ${GIFT_CARD_INPUT_FOCUS_CLASS}`}
            style={getGiftCardInputFocusStyle(accentColor)}
          />
          {Boolean(error) && (
            <p className="ui-px-1 ui-text-text-xs-medium ui-text-red-600">
              {error?.message}
            </p>
          )}
        </div>
      </div>

      <div className="ui-w-full ui-bg-white ui-px-4 ui-pb-6 ui-shadow-[0_-4px_12px_4px_#FFF]">
        <PrimaryButton
          height="ui-h-[52px]"
          buttonText={submitLabel}
          customization={{ backgroundColor: accentColor }}
          isDisabled={!code}
          isLoading={loading}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default GiftCardAddBalance;
